'use client';

import { PropsWithChildren, useState } from 'react';
import { useTranslations } from 'next-intl';
import { messagesIcons, MessageType } from '@/shared/components/primitives/svg';

type MessageBoxProps = {
  type: MessageType;
  title?: string;
  className?: string;
  closable?: boolean;
  onClose?: () => void;
};

const typeStyles: Record<MessageType, string> = {
  error: 'text-error border-red-400',
  warning: 'text-yellow-400 border-yellow-400',
  info: 'text-blue-400 border-blue-400',
  success: 'text-success border-success',
};

export const MessageBox = ({
  type,
  title,
  children,
  className = '',
  closable = true,
  onClose,
}: PropsWithChildren<MessageBoxProps>) => {
  const t = useTranslations('Components.messageBox');
  const [visible, setVisible] = useState(true);

  if ((!children && !title) || !visible) return null;

  const handleClose = () => {
    setVisible(false);
    onClose?.();
  };

  return (
    <div
      className={`relative flex items-start gap-3 border px-4 py-3 text-sm ${typeStyles[type]} ${className} ${closable ? 'pb-10' : ''} `}
      role={type === 'error' ? 'alert' : 'status'}
    >
      <div className='mt-0.5 shrink-0'>{messagesIcons[type]}</div>
      <div>
        {title && <h3 className='mb-1 text-base font-semibold'>{title}</h3>}
        <p>{children}</p>
      </div>
      {closable && (
        <div className='absolute right-2 bottom-2'>
          <button
            onClick={handleClose}
            className='border-border text-text-subtle hover:border-border-hover hover:text-text-hover items-center justify-center rounded-sm border pr-1 pl-1 text-[12px]'
          >
            {t('close')}
          </button>
        </div>
      )}
    </div>
  );
};
